import { useEffect, useMemo, useState } from "react";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useAuth } from "../providers/AuthProvider";
import { createTmaApiClient } from "../services/tmaApiClient";
import { formatWallClockDateTime } from "../utils/wallClockDate";

type TeacherOpsStatus = "idle" | "loading" | "ready" | "empty" | "error";

interface FlowListItem {
  flowNo: string;
  lastStartAt: string | null;
}

interface FlowsPayload {
  flows?: Array<{
    flowNo?: string;
    lastStartAt?: string | null;
  }>;
}

interface MatrixLesson {
  id: string;
  lessonNo: number;
  date: string | null;
  label: string;
}

interface MatrixCell {
  lessonId: string;
  lessonNo: number;
  mark: string | null;
  markLabel: string;
  makeupRequestId: string | null;
  makeupStatus: string | null;
}

interface MatrixStudent {
  id: string;
  fullName: string;
  phone?: string | null;
  cells: MatrixCell[];
}

interface MatrixPayload {
  flowNo: string;
  lessons: MatrixLesson[];
  students: MatrixStudent[];
}

interface QueueItem {
  id: string;
  studentName: string | null;
  flowNo: string | null;
  lessonNo: number | null;
  lessonDate: string | null;
  offeredSlot: string | null;
  status: string;
}

interface MakeupsQueuePayload {
  items?: QueueItem[];
}

const parseIsoTime = (value: string | null | undefined): number | null => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }

  const timestamp = Date.parse(raw);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const pickDefaultFlowNo = (flows: FlowListItem[]): string => {
  if (!flows.length) {
    return "";
  }

  const now = Date.now();
  const started = flows
    .map((flow) => ({
      flowNo: flow.flowNo,
      ts: parseIsoTime(flow.lastStartAt),
    }))
    .filter(
      (item): item is { flowNo: string; ts: number } =>
        typeof item.ts === "number" && item.ts <= now,
    )
    .sort((left, right) => right.ts - left.ts);

  if (started.length) {
    return started[0].flowNo;
  }

  return [...flows].sort(
    (left, right) => Number(right.flowNo) - Number(left.flowNo),
  )[0].flowNo;
};

const pickDefaultLessonId = (lessons: MatrixLesson[]): string => {
  if (!lessons.length) {
    return "";
  }

  const now = Date.now();
  const withDate = lessons
    .map((lesson) => ({
      lesson,
      ts: parseIsoTime(lesson.date),
    }))
    .filter(
      (item): item is { lesson: MatrixLesson; ts: number } =>
        typeof item.ts === "number",
    );

  const started = withDate
    .filter((item) => item.ts <= now)
    .sort(
      (left, right) =>
        right.ts - left.ts || right.lesson.lessonNo - left.lesson.lessonNo,
    );

  if (started.length) {
    return started[0].lesson.id;
  }

  if (withDate.length) {
    return [...withDate].sort(
      (left, right) =>
        right.ts - left.ts || right.lesson.lessonNo - left.lesson.lessonNo,
    )[0].lesson.id;
  }

  return [...lessons].sort((left, right) => right.lessonNo - left.lessonNo)[0]
    .id;
};

const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const normalized = fullName.trim();
  if (!normalized) {
    return { firstName: "Без имени", lastName: "—" };
  }

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "—" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const displayMarkLabel = (markLabel: string | null | undefined): string => {
  if (!markLabel || markLabel === "—") {
    return "—";
  }

  return markLabel;
};

type AttendanceUiMark = "+" | "Н" | "О";

const normalizeCellMark = (cell: MatrixCell | null): AttendanceUiMark => {
  const label = String(cell?.markLabel ?? "").trim();
  if (label === "+" || label === "Н" || label === "О") {
    return label;
  }

  const mark = String(cell?.mark ?? "")
    .trim()
    .toLowerCase();
  if (mark === "absent") {
    return "Н";
  }
  if (mark === "late" || mark === "excused") {
    return "О";
  }

  return "+";
};

const nextCycleMark = (mark: AttendanceUiMark): AttendanceUiMark => {
  if (mark === "+") {
    return "Н";
  }
  if (mark === "Н") {
    return "О";
  }
  return "+";
};

export const TeacherHome = () => {
  const auth = useAuth();
  const apiClient = useMemo(
    () => createTmaApiClient(auth.session),
    [auth.session],
  );

  const [status, setStatus] = useState<TeacherOpsStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const [flows, setFlows] = useState<FlowListItem[]>([]);
  const [selectedFlowNo, setSelectedFlowNo] = useState<string>("");

  const [matrix, setMatrix] = useState<MatrixPayload | null>(null);
  const [selectedLessonId, setSelectedLessonId] = useState<string>("");

  const [bookedMakeups, setBookedMakeups] = useState<QueueItem[]>([]);
  const [markSavingKey, setMarkSavingKey] = useState<string | null>(null);
  const [makeupSavingId, setMakeupSavingId] = useState<string | null>(null);

  const requestWithRetry = async (
    input: Parameters<typeof apiClient.request>[0],
    retries = 1,
  ) => {
    let attempt = 0;
    let lastResult: Awaited<ReturnType<typeof apiClient.request>> | null = null;

    while (attempt <= retries) {
      const result = await apiClient.request(input);
      lastResult = result;

      if (result.ok) {
        return result;
      }

      if (attempt < retries) {
        await sleep(250);
      }

      attempt += 1;
    }

    return lastResult;
  };

  useEffect(() => {
    if (auth.status !== "authenticated" || !auth.session) {
      setStatus("error");
      setErrorMessage(
        "Консоль преподавателя недоступна: отсутствует активная сессия преподавателя.",
      );
      return;
    }

    if (auth.session.user.role !== "teacher") {
      setStatus("error");
      setErrorMessage(
        "Консоль преподавателя доступна только для роли преподавателя.",
      );
      return;
    }

    let cancelled = false;

    const loadFlows = async () => {
      setStatus("loading");
      setErrorMessage(null);

      const result = await apiClient.request({
        method: "GET",
        path: "/ops/schedule/flows",
      });

      if (cancelled) {
        return;
      }

      if (
        !result.ok ||
        !result.responsePayload ||
        typeof result.responsePayload !== "object"
      ) {
        setStatus("error");
        setErrorMessage(
          `Не удалось загрузить группы (${result.status ?? "n/a"}).`,
        );
        return;
      }

      const payload = result.responsePayload as FlowsPayload;
      const flowItems = (payload.flows ?? [])
        .map((item) => {
          const flowNo = String(item.flowNo ?? "").trim();
          if (!flowNo) {
            return null;
          }

          return {
            flowNo,
            lastStartAt:
              typeof item.lastStartAt === "string" ? item.lastStartAt : null,
          };
        })
        .filter((item): item is FlowListItem => Boolean(item));

      setFlows(flowItems);

      if (!flowItems.length) {
        setSelectedFlowNo("");
        setStatus("empty");
        return;
      }

      setSelectedFlowNo((current) =>
        current && flowItems.some((flow) => flow.flowNo === current)
          ? current
          : pickDefaultFlowNo(flowItems),
      );
    };

    void loadFlows();

    return () => {
      cancelled = true;
    };
  }, [apiClient, auth.session, auth.status]);

  const refreshFlowData = async (
    flowNo: string,
    keepLessonSelection: boolean,
  ) => {
    const matrixResult = await requestWithRetry(
      {
        method: "GET",
        path: "/ops/console/attendance/matrix",
        query: { flowNo },
      },
      1,
    );

    if (
      !matrixResult ||
      !matrixResult.ok ||
      !matrixResult.responsePayload ||
      typeof matrixResult.responsePayload !== "object"
    ) {
      setStatus("error");
      setErrorMessage(
        `Не удалось загрузить матрицу посещаемости (${matrixResult?.status ?? "n/a"}).`,
      );
      return;
    }

    const nextMatrix = matrixResult.responsePayload as MatrixPayload;
    setMatrix(nextMatrix);

    if (!nextMatrix.students.length || !nextMatrix.lessons.length) {
      setStatus("empty");
      setBookedMakeups([]);
      setSelectedLessonId("");
      return;
    }

    setSelectedLessonId((current) => {
      if (
        keepLessonSelection &&
        current &&
        nextMatrix.lessons.some((lesson) => lesson.id === current)
      ) {
        return current;
      }

      return pickDefaultLessonId(nextMatrix.lessons);
    });

    const makeupsResult = await requestWithRetry(
      {
        method: "GET",
        path: "/ops/console/makeups/queue",
        query: {
          flowNo,
          status: "booked",
          limit: "100",
          sortBy: "lessonDate",
          sortDir: "asc",
        },
      },
      1,
    );

    if (
      makeupsResult &&
      makeupsResult.ok &&
      makeupsResult.responsePayload &&
      typeof makeupsResult.responsePayload === "object"
    ) {
      const payload = makeupsResult.responsePayload as MakeupsQueuePayload;
      setBookedMakeups(Array.isArray(payload.items) ? payload.items : []);
    } else {
      setBookedMakeups([]);
    }

    setStatus("ready");
  };

  useEffect(() => {
    if (!selectedFlowNo) {
      setMatrix(null);
      setBookedMakeups([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setStatus("loading");
      setErrorMessage(null);

      await refreshFlowData(selectedFlowNo, false);
    };

    void load();

    return () => {
      cancelled = true;
      void cancelled;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlowNo]);

  const selectedLesson = useMemo(
    () =>
      matrix?.lessons.find((lesson) => lesson.id === selectedLessonId) ?? null,
    [matrix?.lessons, selectedLessonId],
  );

  const selectedLessonNo = selectedLesson?.lessonNo ?? null;

  const studentsForSelectedLesson = useMemo(() => {
    if (!matrix || selectedLessonNo === null) {
      return [];
    }

    return [...matrix.students]
      .sort((left, right) =>
        left.fullName.localeCompare(right.fullName, "ru-RU"),
      )
      .map((student) => {
        const cell =
          student.cells.find((item) => item.lessonNo === selectedLessonNo) ??
          null;
        const names = splitFullName(student.fullName);

        return {
          student,
          cell,
          firstName: names.firstName,
          lastName: names.lastName,
          currentMark: normalizeCellMark(cell),
          nextButtonMark: nextCycleMark(normalizeCellMark(cell)),
        };
      });
  }, [matrix, selectedLessonNo]);

  const toggleAttendance = async (
    studentId: string,
    cell: MatrixCell | null,
  ) => {
    if (!selectedFlowNo || !cell) {
      return;
    }

    const toggleKey = `${studentId}:${cell.lessonId}`;
    setMarkSavingKey(toggleKey);
    setInfoMessage(null);
    setErrorMessage(null);

    const targetMark = nextCycleMark(normalizeCellMark(cell));

    const result = await apiClient.request({
      method: "PATCH",
      path: "/ops/console/attendance/mark",
      body: {
        flowNo: selectedFlowNo,
        studentId,
        lessonId: cell.lessonId,
        mark: targetMark,
      },
    });

    if (!result.ok) {
      setErrorMessage(
        `Не удалось обновить отметку (${result.status ?? "n/a"}).`,
      );
      setMarkSavingKey(null);
      return;
    }

    await refreshFlowData(selectedFlowNo, true);
    setInfoMessage(`Поставлена отметка «${targetMark}».`);
    setMarkSavingKey(null);
  };

  const updateMakeupStatus = async (
    makeupId: string,
    mode: "done" | "open",
  ) => {
    setMakeupSavingId(makeupId);
    setInfoMessage(null);
    setErrorMessage(null);

    const result = await apiClient.request({
      method: "PATCH",
      path: `/makeups/${makeupId}/status`,
      body:
        mode === "done"
          ? { status: "done" }
          : { status: "open", offeredSlot: "" },
    });

    if (!result.ok) {
      setErrorMessage(
        `Не удалось обновить отработку (${result.status ?? "n/a"}).`,
      );
      setMakeupSavingId(null);
      return;
    }

    if (selectedFlowNo) {
      await refreshFlowData(selectedFlowNo, true);
    }

    setInfoMessage(
      mode === "done"
        ? "Отработка отмечена как выполненная."
        : "Ученик снова переведен в «Н», слот освобожден для новой записи.",
    );
    setMakeupSavingId(null);
  };

  return (
    <>
      <section className="dashboard-hero" aria-label="Сводка преподавателя">
        <div>
          <h2 className="content-title">Добрый день, Александр</h2>
          <p className="hint">
            Сводка по группам, посещаемости и заявкам на отработку.
          </p>
        </div>

        <div className="summary-grid">
          <div className="summary-card">
            <strong>{flows.length || "—"}</strong>
            <span>Активные группы</span>
          </div>
          <div className="summary-card">
            <strong>{matrix?.students.length ?? "—"}</strong>
            <span>Ученики</span>
          </div>
          <div className="summary-card">
            <strong>{bookedMakeups.length}</strong>
            <span>Заявки на отработку</span>
          </div>
        </div>

        <article className="schedule-card schedule-card--upcoming">
          <div className="schedule-card-header">
            <div>
              <p className="schedule-date">Ближайшее занятие</p>
              <p className="hint">
                Поток {selectedFlowNo || "—"} · Урок{" "}
                {selectedLesson?.lessonNo ?? "—"}
              </p>
            </div>
            <p className="schedule-time">
              {selectedLesson?.date
                ? new Date(selectedLesson.date).toLocaleTimeString("ru-RU", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—"}
            </p>
          </div>
          <p className="hint">
            {selectedLesson?.label || "Тема занятия будет уточнена"}
          </p>
        </article>
      </section>

      <h2 className="content-title">Быстрые действия</h2>
      <p className="hint">
        Выберите группу и урок. Кнопка переключает отметку по кругу: + → Н → О.
        Н открывает заявку на отработку, О закрывает ее.
      </p>

      <div className="teacher-attendance-controls panel">
        <div className="teacher-attendance-fields">
          <label htmlFor="teacher-flow-select">Группа</label>
          <select
            id="teacher-flow-select"
            value={selectedFlowNo}
            onChange={(event) => setSelectedFlowNo(event.target.value)}
            disabled={status === "loading" || !flows.length}
          >
            {!flows.length ? <option value="">Нет групп</option> : null}
            {flows.map((flow) => (
              <option key={flow.flowNo} value={flow.flowNo}>
                {flow.flowNo}
              </option>
            ))}
          </select>

          <label htmlFor="teacher-lesson-select">Урок</label>
          <select
            id="teacher-lesson-select"
            value={selectedLessonId}
            onChange={(event) => setSelectedLessonId(event.target.value)}
            disabled={status === "loading" || !matrix?.lessons?.length}
          >
            {!matrix?.lessons?.length ? (
              <option value="">Нет уроков</option>
            ) : null}
            {(matrix?.lessons ?? []).map((lesson) => (
              <option key={lesson.id} value={lesson.id}>
                Урок {lesson.lessonNo} ·{" "}
                {lesson.date
                  ? new Date(lesson.date).toLocaleString("ru-RU")
                  : "дата не задана"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {infoMessage ? (
        <div className="panel success-panel">{infoMessage}</div>
      ) : null}
      {status === "loading" ? (
        <LoadingSpinner label="Загружаем данные группы и урока..." />
      ) : null}
      {status === "error" ? (
        <div className="alert">
          {errorMessage ?? "Ошибка загрузки консоли преподавателя."}
        </div>
      ) : null}
      {status === "empty" ? (
        <p className="hint">Для выбранной группы пока нет данных.</p>
      ) : null}

      {status === "ready" ? (
        <section className="panel" aria-label="Absence marking table">
          <h3 className="content-title spaced-bottom">
            Ученики на уроке {selectedLessonNo ?? "—"}
          </h3>
          <div className="group-table-scroll">
            <table className="group-table teacher-attendance-table">
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Фамилия</th>
                  <th>Текущая</th>
                  <th>Отметка</th>
                </tr>
              </thead>
              <tbody>
                {studentsForSelectedLesson.map((row) => {
                  const cell = row.cell;
                  const saveKey = `${row.student.id}:${cell?.lessonId || "n/a"}`;
                  const isSaving = markSavingKey === saveKey;

                  return (
                    <tr key={row.student.id}>
                      <td>{row.firstName}</td>
                      <td>{row.lastName}</td>
                      <td>{displayMarkLabel(cell?.markLabel)}</td>
                      <td>
                        <button
                          type="button"
                          className="action-button"
                          disabled={!cell || isSaving}
                          onClick={() => {
                            void toggleAttendance(row.student.id, cell);
                          }}
                        >
                          {isSaving ? "Сохраняем..." : row.nextButtonMark}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {status === "ready" ? (
        <section className="panel" aria-label="Booked makeup students section">
          <h3 className="content-title spaced-bottom">
            Должны прийти на отработку
          </h3>
          {!bookedMakeups.length ? (
            <p className="hint">
              В этой группе нет активных записей на отработку.
            </p>
          ) : null}

          {bookedMakeups.length ? (
            <div className="group-table-scroll">
              <table className="group-table teacher-makeup-table">
                <thead>
                  <tr>
                    <th>Ученик</th>
                    <th>Урок</th>
                    <th>Слот</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {bookedMakeups.map((item) => {
                    const slotLabel = item.offeredSlot
                      ? formatWallClockDateTime(item.offeredSlot)
                      : "не назначен";
                    const isSaving = makeupSavingId === item.id;

                    return (
                      <tr key={item.id}>
                        <td>{item.studentName || "Без имени"}</td>
                        <td>{item.lessonNo ? `Урок ${item.lessonNo}` : "—"}</td>
                        <td>{slotLabel}</td>
                        <td className="teacher-makeup-actions">
                          <button
                            type="button"
                            className="action-button"
                            disabled={isSaving}
                            onClick={() => {
                              void updateMakeupStatus(item.id, "done");
                            }}
                          >
                            Отработка закрыта
                          </button>
                          <button
                            type="button"
                            className="action-button"
                            disabled={isSaving}
                            onClick={() => {
                              void updateMakeupStatus(item.id, "open");
                            }}
                          >
                            Снова Н
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
};
